import React, { useEffect, useMemo, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { TrendingUp, Share2, RefreshCw, ExternalLink, Play, Square, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import NewsletterSignup from "@/components/NewsletterSignup";

const PALETTE = ["#10B981", "#E0794B", "#4C8DFF", "#A78BFA", "#22D3EE", "#FB923C",
  "#F472B6", "#FBBF24", "#34D399", "#C084FC", "#F87171"];
const OTHER_COLOR = "#64748B";

// Friendly display name from the CSV model string: drops trailing qualifier in
// parentheses (e.g. "GPT-5.5 Pro (xhigh)" -> "GPT-5.5 Pro"). Falls back to company
// when the CSV has no model name. Never invents a name.
function friendlyName(model, company) {
  const m = (model || "").trim();
  if (!m) return company;
  return m.replace(/\s*\([^)]*\)\s*$/, "").trim() || company;
}

function buildGroups(points) {
  const counts = {};
  points.forEach((p) => { counts[p.company] = (counts[p.company] || 0) + 1; });
  const ranked = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  const top = ranked.slice(0, PALETTE.length);
  const colorOf = {};
  top.forEach((c, i) => { colorOf[c] = PALETTE[i]; });
  const groupName = (c) => (colorOf[c] ? c : "Other");
  const order = [...top];
  if (ranked.length > top.length) order.push("Other");
  return { order, colorOf: { ...colorOf, Other: OTHER_COLOR }, groupName };
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-white/15 bg-[#0B0F19] px-3 py-2 shadow-xl text-xs" data-testid="chart-tooltip">
      <div className="font-display font-bold text-white text-sm">{p.model || "(unnamed model)"}</div>
      <div className="text-[#94A3B8] mt-1">{p.company}</div>
      <div className="mt-1.5 flex items-center gap-3">
        <span className="text-[#00F0FF] font-mono-tech font-bold">{p.score}</span>
        <span className="text-[#64748B]">{p.date}</span>
      </div>
    </div>
  );
}

export default function AIProgress({ embedded = false }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState({});
  const [sharing, setSharing] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  const [cutoff, setCutoff] = useState(null); // null = show full chart
  const [playing, setPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const cardRef = useRef(null);
  const timerRef = useRef(null);

  const prefersReduced = useMemo(
    () => typeof window !== "undefined" && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    api.get("/ai-progress").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const groups = useMemo(() => (data ? buildGroups(data.points) : null), [data]);

  const series = useMemo(() => {
    if (!data || !groups) return [];
    const byGroup = {};
    groups.order.forEach((g) => { byGroup[g] = []; });
    data.points.forEach((p) => { byGroup[groups.groupName(p.company)].push(p); });
    return groups.order.map((g) => ({ name: g, color: groups.colorOf[g], points: byGroup[g] }));
  }, [data, groups]);

  // Record-setting models (each set a new all-time-best score), in time order.
  const records = useMemo(() => {
    if (!data?.points?.length) return [];
    const recs = [];
    let mx = -Infinity;
    for (const p of data.points) {
      if (p.score > mx) { mx = p.score; recs.push({ ...p, idx: recs.length, friendly: friendlyName(p.model, p.company) }); }
    }
    return recs;
  }, [data]);

  const frontier = useMemo(() => {
    if (!records.length || !data) return [];
    // "flat-then-step-up" running-max line: rises or holds flat, never falls.
    const lastTs = data.points[data.points.length - 1].ts;
    const step = [];
    for (let k = 0; k < records.length; k++) {
      step.push({ ts: records[k].ts, score: records[k].score });
      const nextTs = k + 1 < records.length ? records[k + 1].ts : lastTs;
      if (nextTs > records[k].ts) step.push({ ts: nextTs, score: records[k].score });
    }
    return step;
  }, [records, data]);

  // Which record-setters to label (thinned by min date-gap; fewer on mobile),
  // always keeping the final peak. dy alternates to reduce overlap.
  const labelInfo = useMemo(() => {
    if (!records.length) return { set: new Set(), dy: {}, finalIdx: null };
    const minMs = (isMobile ? 300 : 150) * 86400000;
    // Best (highest score) record per unique date, in time order — avoids labeling
    // multiple same-day records (e.g. Llama variants released together).
    const byTs = new Map();
    records.forEach((r) => { const c = byTs.get(r.ts); if (!c || r.score > c.score) byTs.set(r.ts, r); });
    const uniq = [...byTs.values()].sort((a, b) => a.ts - b.ts);
    // Thin by date-gap AND skip repeats of the same display name.
    const picked = [];
    let lastTs = -Infinity, lastName = null;
    for (const r of uniq) {
      if (r.ts - lastTs >= minMs && r.friendly !== lastName) {
        picked.push(r); lastTs = r.ts; lastName = r.friendly;
      }
    }
    const finalR = uniq[uniq.length - 1];
    if (!picked.some((p) => p.idx === finalR.idx)) {
      while (picked.length && finalR.ts - picked[picked.length - 1].ts < minMs) picked.pop();
      picked.push(finalR);
    }
    const set = new Set(picked.map((p) => p.idx));
    const dy = {};
    picked.forEach((p, i) => { dy[p.idx] = i % 2 === 0 ? -14 : -28; });
    return { set, dy, finalIdx: finalR.idx };
  }, [records, isMobile]);

  const renderRecord = (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const labeled = labelInfo.set.has(payload.idx);
    const isFinal = payload.idx === labelInfo.finalIdx;
    return (
      <g>
        <circle cx={cx} cy={cy} r={4} fill="#00F0FF" stroke="#0B0F19" strokeWidth={1.5} />
        {labeled && (
          <text x={cx + (isFinal ? -6 : 0)} y={cy + (labelInfo.dy[payload.idx] || -14)}
            textAnchor={isFinal ? "end" : "middle"} fontSize={11} fontWeight={600}
            fill="#E2E8F0" stroke="#0B0F19" strokeWidth={3}
            style={{ paintOrder: "stroke", pointerEvents: "none" }}>
            {payload.friendly}
          </text>
        )}
      </g>
    );
  };

  const yearTicks = useMemo(() => {
    if (!data?.points?.length) return [];
    const y0 = new Date(data.points[0].ts).getUTCFullYear();
    const y1 = new Date(data.points[data.points.length - 1].ts).getUTCFullYear();
    const t = [];
    for (let y = y0; y <= y1 + 1; y++) t.push(Date.UTC(y, 0, 1));
    return t;
  }, [data]);

  const toggle = (name) => setHidden((h) => ({ ...h, [name]: !h[name] }));

  // ---- Optional "play the timeline" animation ----
  // Time-based rAF (always finishes in DURATION regardless of render speed) with
  // throttled state updates so it stays smooth on mobile.
  const stopAnim = () => { if (timerRef.current) { cancelAnimationFrame(timerRef.current); timerRef.current = null; } };
  useEffect(() => stopAnim, []);

  const play = () => {
    if (!data?.points?.length) return;
    stopAnim();
    const tsMin = data.points[0].ts;
    const tsMax = data.points[data.points.length - 1].ts;
    const DURATION = 3800;
    let start = null, lastUpdate = 0;
    setPlaying(true);
    setCutoff(tsMin);
    const tick = (now) => {
      if (start == null) start = now;
      const prog = Math.min(1, (now - start) / DURATION);
      if (prog >= 1) { stopAnim(); setCutoff(null); setPlaying(false); setHasPlayed(true); return; }
      if (now - lastUpdate >= 90) { setCutoff(tsMin + (tsMax - tsMin) * prog); lastUpdate = now; }
      timerRef.current = requestAnimationFrame(tick);
    };
    timerRef.current = requestAnimationFrame(tick);
  };

  const skip = () => { stopAnim(); setCutoff(null); setPlaying(false); setHasPlayed(true); };

  const share = async () => {
    if (!cardRef.current) return;
    setSharing(true);
    try {
      const url = await toPng(cardRef.current, { backgroundColor: "#0B0F19", pixelRatio: 2, cacheBust: true });
      const a = document.createElement("a");
      a.download = "ai-progress-worldaiinfo.png";
      a.href = url;
      a.click();
      toast.success("Chart image downloaded — share it anywhere!");
    } catch {
      toast.error("Could not generate image");
    } finally { setSharing(false); }
  };

  if (!data) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-[#94A3B8]" data-testid="ai-progress-loading">
        <RefreshCw className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  // Fixed axis domains so points appear in place during the animation (no jitter).
  const xDomain = [data.points[0].ts, data.points[data.points.length - 1].ts];
  const scores = data.points.map((p) => p.score);
  const yDomain = [Math.min(...scores) - 5, Math.max(...scores) + 14];
  // Apply the animation cutoff (null = full chart).
  const showSeries = series.map((s) => ({ ...s, data: cutoff == null ? s.points : s.points.filter((p) => p.ts <= cutoff) }));
  const showFrontier = cutoff == null ? frontier : frontier.filter((p) => p.ts <= cutoff);
  const showRecords = cutoff == null ? records : records.filter((r) => r.ts <= cutoff);

  return (
    <div className={embedded ? "" : "max-w-6xl mx-auto px-6 md:px-12 py-10"} data-testid="ai-progress-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        {!embedded && (
        <div>
          <div className="inline-flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-[#00F0FF]" />
            <span className="font-mono-tech text-xs uppercase text-[#00F0FF] tracking-wider">
              {data.count} models · {data.date_min?.slice(0, 4)}–{data.date_max?.slice(0, 4)}
            </span>
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            How fast is AI actually improving?
          </h1>
          <p className="text-[#94A3B8] mt-3 text-base max-w-2xl">
            Each dot is an AI model. Higher = more capable. Watch the climb.
          </p>
        </div>
        )}
        <div className="flex items-center gap-2 shrink-0">
          {!prefersReduced && (
            <button onClick={playing ? skip : play} data-testid="ai-progress-play"
              className="inline-flex items-center gap-2 border border-[#00F0FF]/40 text-[#00F0FF] font-semibold px-4 py-2.5 rounded-md hover:bg-[#00F0FF]/10 transition">
              {playing ? <><Square className="w-4 h-4" /> Skip</>
                : hasPlayed ? <><RotateCcw className="w-4 h-4" /> Replay</>
                : <><Play className="w-4 h-4" /> Play timeline</>}
            </button>
          )}
          <button onClick={share} disabled={sharing} data-testid="ai-progress-share"
            className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#04121a] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 disabled:opacity-50 transition">
            <Share2 className="w-4 h-4" /> {sharing ? "Generating…" : "Share"}
          </button>
        </div>
      </div>

      {/* Captured card */}
      <div ref={cardRef} className="card-surface rounded-3xl p-4 sm:p-6 mt-8" data-testid="ai-progress-chart-card">
        <div className="hidden sm:flex items-center justify-between mb-3 px-2">
          <span className="font-display font-bold text-white text-sm">AI Capability Over Time — Epoch AI ECI</span>
          <span className="font-mono-tech text-xs text-[#00F0FF]">worldaiinfo.com</span>
        </div>
        <div className="w-full" style={{ height: 480 }} data-testid="ai-progress-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 28, right: 20, bottom: 20, left: 4 }}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis
                type="number" dataKey="ts" name="date" domain={xDomain} allowDataOverflow
                ticks={yearTicks} scale="time"
                tickFormatter={(t) => new Date(t).getUTCFullYear()}
                tick={{ fill: "#94A3B8", fontSize: 12 }} stroke="#334155"
              />
              <YAxis
                type="number" dataKey="score" name="score" domain={yDomain} allowDataOverflow
                tick={{ fill: "#94A3B8", fontSize: 12 }} stroke="#334155"
                label={{ value: "ECI capability score", angle: -90, position: "insideLeft", fill: "#64748B", fontSize: 12, style: { textAnchor: "middle" } }}
              />
              <Tooltip content={<ChartTooltip />} cursor={{ strokeDasharray: "3 3", stroke: "#334155" }} />
              {showSeries.map((s) => (
                <Scatter key={s.name} name={s.name} data={s.data} fill={s.color}
                  hide={!!hidden[s.name]} fillOpacity={0.38} isAnimationActive={false} />
              ))}
              <Scatter data={showFrontier} line={{ stroke: "#00F0FF", strokeWidth: 2.5 }}
                shape={() => null} legendType="none" isAnimationActive={false} data-testid="frontier-line" />
              <Scatter data={showRecords} shape={renderRecord} legendType="none"
                isAnimationActive={false} data-testid="frontier-records" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        {/* Legend (clickable to toggle) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 px-2" data-testid="ai-progress-legend">
          <span className="inline-flex items-center gap-1.5 text-xs" data-testid="legend-frontier">
            <span className="w-5 h-0.5 rounded" style={{ background: "#00F0FF" }} />
            <span className="text-[#00F0FF] font-medium">Frontier (best-so-far)</span>
          </span>
          {series.map((s) => (
            <button key={s.name} onClick={() => toggle(s.name)} data-testid={`legend-${s.name}`}
              className={`inline-flex items-center gap-1.5 text-xs transition ${hidden[s.name] ? "opacity-40 line-through" : "opacity-100"}`}>
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
              <span className="text-[#CBD5E1]">{s.name}</span>
            </button>
          ))}
        </div>

        {/* Source citation — required by CC-BY, always visible */}
        <div className="mt-4 pt-3 border-t border-white/[0.06] px-2" data-testid="ai-progress-source">
          <a href={data.source_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-[#00F0FF] transition">
            Data: Epoch AI Capabilities Index ({data.license}) <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>

      <p className="text-xs text-[#64748B] mt-4 max-w-2xl">
        The cyan <span className="text-[#00F0FF]">frontier line</span> traces the highest capability score reached at any point in time — it only rises or holds flat, never falls. Individual model dots are faded underneath so the real, unmodified data stays visible.
        Only models with a published capability score and release date are plotted — nothing is estimated or invented.
        Tap a company in the legend to show/hide it. Data licensed under {data.license} by Epoch AI.
      </p>

      {!embedded && (
        <div className="mt-10">
          <NewsletterSignup variant="inline" source="ai_progress" testid="ai-progress-newsletter" />
        </div>
      )}
    </div>
  );
}
